import { useEffect, useMemo, useState } from 'react';
import { PaginationWrapper, StyledPagination } from './styled.pagination';
import { PaginationProps, PaginationConfig } from './pagination.props';
import StyledTooltip from '../tooltip';
import './pagination.css';

export default function Pagination({
  id,
  className,
  rowsPerPage,
  activePage,
  handleChange,
  setOffset,
  totalItems,
  updatePageAsPerSearch,
  setDataToDelete,
  sentPrevious,
  'data-automation-id': dataAutomationId,
}: PaginationProps) {
  const [value, setValue] = useState<number>();
  const [paginationConfig, setPaginationConfig] = useState<PaginationConfig>({
    activePage: activePage || 1,
    rowsPerPage: rowsPerPage || 10,
  });

  const totalPages = useMemo(
    () => totalItems && Math.ceil(totalItems / paginationConfig.rowsPerPage),
    [totalItems, paginationConfig.rowsPerPage],
  );

  const currentPage = paginationConfig.activePage;

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    if (!totalPages || totalPages < 1) return pages;
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 2);
    const right = Math.min(totalPages - 1, currentPage + 2);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i += 1) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [totalPages, currentPage]);

  const handlePageChange = (actionType: string, pageNo?: number) => {
    if (updatePageAsPerSearch) {
      setPaginationConfig({ ...paginationConfig, activePage: 1 });
    } else {
      const newConfig = {
        activePage: paginationConfig.activePage,
        rowsPerPage: paginationConfig.rowsPerPage,
      };
      switch (actionType) {
        case 'prev': {
          if (paginationConfig.activePage !== 1)
            newConfig.activePage = paginationConfig.activePage - 1;
          const prevOffSet = (newConfig.activePage - 1) * newConfig.rowsPerPage;
          setOffset(prevOffSet);
          setValue(prevOffSet);
          break;
        }
        case 'custom': {
          if (totalPages) {
            if (pageNo && pageNo > 0 && pageNo <= totalPages) {
              newConfig.activePage = pageNo;
              const pageOffSet = (pageNo - 1) * newConfig.rowsPerPage;
              setOffset(pageOffSet);
              setValue(pageOffSet);
              setDataToDelete?.([]);
            }
          }
          break;
        }
        case 'next': {
          if (paginationConfig.activePage !== totalPages) {
            newConfig.activePage = paginationConfig.activePage + 1;
            const nextOffSet = paginationConfig.activePage * newConfig.rowsPerPage;
            setOffset(nextOffSet);
            setValue(nextOffSet);
          }
          break;
        }
        default:
          break;
      }
      if (paginationConfig.activePage !== newConfig.activePage) {
        setPaginationConfig(newConfig);
        handleChange?.(newConfig);
      }
    }
  };

  useEffect(() => {
    if (sentPrevious) {
      handlePageChange('prev');
    }
  }, [sentPrevious]);

  useEffect(() => {}, [
    paginationConfig.activePage,
    paginationConfig.rowsPerPage,
    totalPages,
    setOffset,
  ]);

  return totalItems && totalPages && totalPages > 1 ? (
    <PaginationWrapper id={id} className={className} data-automation-id={dataAutomationId}>
      <StyledPagination className="justify-between flex w-full">
        <StyledTooltip id="prev-page-button" title="Previous page">
          <div>
            <button
              type="button"
              id="prev-page-button"
              disabled={paginationConfig.activePage === 1}
              className={`border-1 border-solid  w-[59] h-[32] rounded-lg px-[16px] py-[10px]  ${
                paginationConfig.activePage
              } ${paginationConfig.activePage === 1 ? 'bg-gray-200 cursor-not-allowed' : ' bg-white hover:cursor-pointer'}`}
              onClick={() => handlePageChange('prev')}
            >
              <div className="text-[13px]">Prev</div>
            </button>
          </div>
        </StyledTooltip>
        <div className="page-numbers-wrapper flex items-center gap-1">
          {pageNumbers.map((page) =>
            page === '...' ? (
              <span key="ellipsis" className="px-1 text-gray-500">
                .....
              </span>
            ) : (
              <button
                type="button"
                key={page}
                className={`flex items-center justify-center min-w-[32px] h-[32px] rounded-md px-[10px] border-1 border-solid ${
                  page === paginationConfig.activePage ? 'bg-[#0A8BC7] text-white' : ''
                }`}
                onClick={() => typeof page === 'number' && handlePageChange('custom', page)}
              >
                {page}
              </button>
            ),
          )}
        </div>
        <StyledTooltip id="next-page-button" title="Next page">
          <div>
            <button
              type="button"
              value={value}
              id="next-page-button"
              disabled={paginationConfig.activePage === totalPages}
              className={`border-1 border-solid w-[59] h-[32] rounded-lg px-[16px] py-[10px]  ${
                paginationConfig.activePage === totalPages
                  ? 'bg-gray-200 cursor-not-allowed'
                  : 'bg-white hover:cursor-pointer'
              }`}
              onClick={() => handlePageChange('next')}
            >
              <div className="text-[13px]">Next</div>
            </button>
          </div>
        </StyledTooltip>
      </StyledPagination>
    </PaginationWrapper>
  ) : null;
}

Pagination.defaultProps = {
  numberOfRows: 25,
  handleChange: (config: PaginationConfig) => config,
};
